import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  getParentChildren,
  getChildAttendanceHistory,
  getAthleteMetrics,
  getChildPointsSummary,
  getChildProducts,
  getChildSessions,
  getChildEventInvitations,
  getParentDiscountStatus,
  resumeChildPayment,
  type ParentChild,
  type AttendanceRecord,
  type AthleteMetrics,
  type PointsSummary,
  type AthleteProduct,
  type EventInvitation,
} from "../../../services/api";
import { listUserBadges, type UserBadge } from "../../../services/badges";
import { isIndividualChild } from "../../../constants/individualClub";
import { useAuth } from "../../../context/AuthContext";
import MobileAppLayout from "../../../layouts/MobileAppLayout";
import {
  GameCard,
  GameHero,
  StatTile,
  RingProgress,
  LevelBar,
  HudChip,
  Skeleton,
  GameIcon,
} from "../../../components/game";
import { formatDate } from "../../../lib/dateFormat";

type ViewType = "home" | "progress" | "sessions" | "attendance" | "points" | "metrics";

const TIER_RING: Record<string, string> = {
  bronze: "from-amber-500 to-orange-700",
  silver: "from-slate-300 to-slate-500",
  gold: "from-amber-300 to-yellow-500",
  platinum: "from-cyan-300 to-sky-500",
  diamond: "from-indigo-300 to-violet-500",
};

export default function AppParentDashboard() {
  const { t } = useTranslation();
  const { activeChildId, setActiveChildId, setActiveChildIndividual } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [children, setChildren] = useState<ParentChild[]>([]);
  const [loading, setLoading] = useState(true);
  const [childrenError, setChildrenError] = useState(false);
  const [selectedChild, setSelectedChild] = useState<ParentChild | null>(null);
  const [siblingDiscount, setSiblingDiscount] = useState<number>(0);
  const [resuming, setResuming] = useState(false);

  const handleResumePayment = async (childId: string) => {
    setResuming(true);
    try {
      const url = await resumeChildPayment(childId);
      if (url) window.location.href = url;
      else setResuming(false);
    } catch {
      setResuming(false);
    }
  };

  // Data for home view
  const [pendingPayments, setPendingPayments] = useState<AthleteProduct[]>([]);
  const [clubPaymentMethods, setClubPaymentMethods] = useState<string[]>([]);

  // Gamification (shared by home snapshot + progress view + points view)
  const [pointsData, setPointsData] = useState<PointsSummary | null>(null);
  const [childBadges, setChildBadges] = useState<UserBadge[]>([]);
  const [gamiLoading, setGamiLoading] = useState(false);

  // Data for other sub-views
  const [attendanceData, setAttendanceData] = useState<{
    records: AttendanceRecord[];
    stats: { present: number; late: number; absent: number; excused: number; total: number; attendance_rate: number };
  } | null>(null);
  const [metricsData, setMetricsData] = useState<AthleteMetrics[]>([]);
  const [sessionsData, setSessionsData] = useState<any[]>([]);
  const [viewLoading, setViewLoading] = useState(false);

  // Rich home-screen extras for the selected child.
  const [homeMetric, setHomeMetric] = useState<AthleteMetrics | null>(null);
  const [homeAttendance, setHomeAttendance] = useState<{ present: number; total: number; attendance_rate: number } | null>(null);
  const [homeNextSession, setHomeNextSession] = useState<any | null>(null);
  const [homeUpcomingCount, setHomeUpcomingCount] = useState(0);
  const [homeEvents, setHomeEvents] = useState<EventInvitation[]>([]);

  // Determine current view from URL
  const getView = (): ViewType => {
    const path = location.pathname;
    if (path.includes("/progress")) return "progress";
    if (path.includes("/sessions")) return "sessions";
    if (path.includes("/attendance")) return "attendance";
    if (path.includes("/points")) return "points";
    if (path.includes("/metrics")) return "metrics";
    return "home";
  };

  const currentView = getView();
  const childIdFromUrl = searchParams.get("child");

  useEffect(() => {
    loadChildren();
  }, []);

  useEffect(() => {
    // If URL has a child param, select that child
    if (childIdFromUrl && children.length > 0) {
      const child = children.find(c => c.athlete_id === childIdFromUrl);
      if (child) setSelectedChild(child);
    }
  }, [childIdFromUrl, children]);

  // Keep the shared "is the active child clubless?" flag in sync so the
  // bottom nav (in the layout) adapts to the selected child.
  useEffect(() => {
    setActiveChildIndividual(isIndividualChild(selectedChild));
  }, [selectedChild, setActiveChildIndividual]);

  useEffect(() => {
    // Load data for current view when child or view changes
    if (!selectedChild) return;
    // Gamification data powers the home snapshot, the progress view and the
    // points view, so fetch it whenever those surfaces are shown.
    if (currentView === "home" || currentView === "progress" || currentView === "points") {
      loadGamification();
    }
    if (currentView === "home") {
      loadHomeData();
      loadHomeExtras();
    } else if (currentView !== "progress" && currentView !== "points") {
      loadViewData();
    }
  }, [selectedChild, currentView]);

  // Fetch the richer home-screen data (next training, latest metrics, attendance
  // summary, upcoming events) for the selected child. Each is best-effort.
  const loadHomeExtras = async () => {
    if (!selectedChild) return;
    const childId = selectedChild.athlete_id;
    const teamId = selectedChild.athlete.athlete_profiles?.team_id || null;
    const [metrics, att, sessions, events] = await Promise.all([
      getAthleteMetrics(childId).catch(() => [] as AthleteMetrics[]),
      getChildAttendanceHistory(childId).catch(() => null),
      teamId ? getChildSessions(teamId).catch(() => [] as any[]) : Promise.resolve([] as any[]),
      getChildEventInvitations(childId).catch(() => ({ invitations: [] as EventInvitation[] })),
    ]);
    setHomeMetric(metrics[0] || null);
    setHomeAttendance(att?.stats ? { present: att.stats.present, total: att.stats.total, attendance_rate: att.stats.attendance_rate } : null);
    const now = Date.now();
    const upcoming = (sessions || [])
      .filter((s: any) => new Date(s.start_at).getTime() > now)
      .sort((a: any, b: any) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime());
    setHomeNextSession(upcoming[0] || null);
    setHomeUpcomingCount(upcoming.length);
    setHomeEvents(
      (events.invitations || [])
        .filter((i) => i.status !== "declined" && i.event && new Date(i.event.start_date).getTime() > now)
        .sort((a, b) => new Date(a.event!.start_date).getTime() - new Date(b.event!.start_date).getTime())
        .slice(0, 3),
    );
  };

  const loadChildren = async () => {
    setLoading(true);
    setChildrenError(false);
    try {
      const data = await getParentChildren();
      setChildren(data);
      if (data.length > 0) {
        // Prefer the URL's ?child= (deep link), then the remembered child, else
        // the first. Persist the resolved choice so it survives navigation.
        const urlChild = searchParams.get("child");
        const resolved =
          (urlChild && data.find(c => c.athlete_id === urlChild)) ||
          (activeChildId && data.find(c => c.athlete_id === activeChildId)) ||
          data[0];
        setSelectedChild(resolved);
        setActiveChildId(resolved.athlete_id);
      }
      getParentDiscountStatus()
        .then((d) => setSiblingDiscount(d.discount_percent))
        .catch(() => {});
    } catch (err) {
      console.error("Failed to load children:", err);
      setChildrenError(true);
    }
    setLoading(false);
  };

  const loadGamification = async () => {
    if (!selectedChild) return;
    setGamiLoading(true);
    try {
      const [points, badges] = await Promise.all([
        getChildPointsSummary(selectedChild.athlete_id),
        listUserBadges(selectedChild.athlete_id).catch(() => [] as UserBadge[]),
      ]);
      setPointsData(points);
      setChildBadges(badges);
    } catch (err) {
      console.error("Failed to load gamification data:", err);
    }
    setGamiLoading(false);
  };

  const loadHomeData = async () => {
    if (!selectedChild) return;
    try {
      const data = await getChildProducts(selectedChild.athlete_id);
      setPendingPayments(data.products.filter(p => p.payment_status !== "paid"));
      setClubPaymentMethods(data.club?.payment_methods || []);
    } catch (err) {
      console.error("Failed to load home data:", err);
    }
  };

  const loadViewData = async () => {
    if (!selectedChild) return;
    setViewLoading(true);
    try {
      if (currentView === "attendance") {
        const data = await getChildAttendanceHistory(selectedChild.athlete_id);
        setAttendanceData({ records: data.attendance, stats: data.stats });
      } else if (currentView === "metrics") {
        const data = await getAthleteMetrics(selectedChild.athlete_id);
        setMetricsData(data);
      } else if (currentView === "sessions") {
        const teamId = selectedChild.athlete.athlete_profiles?.team_id;
        if (teamId) {
          const data = await getChildSessions(teamId);
          setSessionsData(data);
        }
      }
    } catch (err) {
      console.error("Failed to load view data:", err);
    }
    setViewLoading(false);
  };

  const formatCurrency = (cents: number, currency: string = "RON") => {
    return new Intl.NumberFormat("ro-RO", { style: "currency", currency }).format(cents / 100);
  };

  if (loading) {
    return (
      <MobileAppLayout>
        <div className="px-4 py-6 space-y-4">
          <Skeleton className="h-36 rounded-3xl" />
          <div className="grid grid-cols-3 gap-3">
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
          </div>
          <Skeleton className="h-40 rounded-2xl" />
        </div>
      </MobileAppLayout>
    );
  }

  // A failed children fetch used to fall through to the "no children" empty
  // state — indistinguishable from a parent who genuinely has none. Surface a
  // visible error + retry instead.
  if (childrenError) {
    return (
      <MobileAppLayout>
        <div className="px-4 py-6">
          <GameCard className="p-6 text-center">
            <h3 className="text-base font-bold text-slate-800 mb-1">{t("common.loadErrorTitle", "Ceva n-a mers")}</h3>
            <p className="text-sm text-slate-500">
              {t("common.loadError", "Nu am putut încărca datele. Verifică conexiunea și încearcă din nou.")}
            </p>
            <button
              onClick={loadChildren}
              className="mt-3 px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white"
            >
              {t("common.retry", "Reîncearcă")}
            </button>
          </GameCard>
        </div>
      </MobileAppLayout>
    );
  }

  if (children.length === 0) {
    return (
      <MobileAppLayout>
        <div className="px-4 py-12 text-center">
          <div className="w-20 h-20 mx-auto mb-4 rounded-3xl bg-indigo-50 grid place-items-center">
            <svg className="w-10 h-10 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">
            {t("parent.noChildren", "Nu aveți copii înregistrați")}
          </h2>
          <p className="text-slate-500 mb-6">
            {t("parent.noChildrenDesc", "Contactați administratorul clubului pentru a vă asocia contul cu sportivul.")}
          </p>
          <Link
            to="/app/parent/add-child"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold transition"
          >
            <span className="text-lg leading-none">＋</span>
            {t("parent.addChild", "Adaugă un copil")}
          </Link>
        </div>
      </MobileAppLayout>
    );
  }

  const child = selectedChild?.athlete;
  const childName = child?.full_name || t("parent.child", "Copilul dvs.");
  // A clubless ("individual") child has no team, trainings, attendance, events
  // or coach-recorded metrics — used to gate those surfaces across the screen.
  const individual = isIndividualChild(selectedChild);
  const firstName = childName.split(" ")[0];
  const level = pointsData?.level;

  const getStatusConfig = (status: string) => {
    switch (status) {
      case "present":
        return { label: t("attendance.present", "Prezent"), bg: "bg-emerald-100", text: "text-emerald-700" };
      case "late":
        return { label: t("attendance.late", "Întârziat"), bg: "bg-amber-100", text: "text-amber-700" };
      case "absent":
        return { label: t("attendance.absent", "Absent"), bg: "bg-red-100", text: "text-red-700" };
      case "excused":
        return { label: t("attendance.excused", "Motivat"), bg: "bg-blue-100", text: "text-blue-700" };
      default:
        return { label: status, bg: "bg-slate-100", text: "text-slate-700" };
    }
  };

  const getSourceLabel = (source: string) => {
    switch (source) {
      case "attendance": return t("points.attendance", "Prezență");
      case "quiz": return t("points.quiz", "Quiz");
      case "video": return t("points.video", "Video");
      case "reward": return t("points.reward", "Recompensă");
      case "competition": return t("points.competition", "Competiție");
      case "event": return t("points.event", "Eveniment");
      case "bonus":
      case "manual":
      case "admin_adjustment": return t("points.bonus", "Bonus");
      // Repurposed enum for weekly/monthly leaderboard podium prizes.
      case "bonus_vulnerable": return t("points.leaderboardPrize", "Premiu de clasament");
      default: return t("points.other", "Puncte");
    }
  };

  // ── Child selector (shared) ───────────────────────────────────────
  const childSelector = children.length > 1 && (
    <select
      value={selectedChild?.athlete_id || ""}
      onChange={e => {
        const c = children.find(ch => ch.athlete_id === e.target.value);
        setSelectedChild(c || null);
        if (c) setActiveChildId(c.athlete_id);
      }}
      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
    >
      {children.map(c => (
        <option key={c.athlete_id} value={c.athlete_id}>{c.athlete.full_name}</option>
      ))}
    </select>
  );

  // ── Sub-views ─────────────────────────────────────────────────────
  const renderSubView = () => {
    if (currentView === "progress") return renderProgressView();
    if (viewLoading) {
      return (
        <div className="space-y-4">
          <Skeleton className="h-40 rounded-2xl" />
          <Skeleton className="h-24 rounded-2xl" />
          <Skeleton className="h-24 rounded-2xl" />
        </div>
      );
    }
    switch (currentView) {
      case "attendance": return renderAttendanceView();
      case "metrics": return renderMetricsView();
      case "sessions": return renderSessionsView();
      case "points": return renderPointsView();
      default: return null;
    }
  };

  // ── Progress (gamification) view ──────────────────────────────────
  const recentQuizzes = (pointsData?.recent_history || []).filter(e => e.source === "quiz").slice(0, 6);

  const renderProgressView = () => {
    if (gamiLoading && !pointsData) {
      return (
        <div className="space-y-4">
          <Skeleton className="h-44 rounded-2xl" />
          <Skeleton className="h-28 rounded-2xl" />
        </div>
      );
    }
    return (
      <div className="space-y-5">
        {/* Level hero */}
        <GameCard className="p-5">
          <div className="flex items-center gap-5">
            <RingProgress pct={level?.progress_pct ?? 0} size={92} stroke={9} color="#6366f1">
              <div className="text-center leading-none">
                <div className="text-[10px] font-bold text-slate-400 uppercase">{t("parent.level", "Nivel")}</div>
                <div className="text-3xl font-extrabold text-slate-800 tabular-nums">{level?.level ?? 1}</div>
              </div>
            </RingProgress>
            <div className="min-w-0 flex-1">
              {level?.title && <div className="text-base font-extrabold text-slate-800 truncate">{level.title}</div>}
              <div className="text-sm text-slate-500 mt-0.5">
                {level
                  ? `${level.xp_into_level}/${level.xp_for_next} ${t("parent.xpToNext", "XP până la nivelul următor")}`
                  : t("parent.noPoints", "Nu există puncte înregistrate")}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <HudChip icon="star" value={pointsData?.total ?? 0} tone="amber" />
                <HudChip icon="medal" value={childBadges.length} tone="orange" />
              </div>
            </div>
          </div>
        </GameCard>

        {/* Points quick-stats */}
        <div className="grid grid-cols-3 gap-3">
          <StatTile icon="star" label={t("points.thisWeek", "Săptămâna asta")} value={pointsData?.this_week ?? 0} color="#f59e0b" />
          <StatTile icon="bolt" label={t("points.thisMonth", "Luna asta")} value={pointsData?.this_month ?? 0} color="#6366f1" />
          <StatTile icon="trophy" label={t("points.total", "Total puncte")} value={pointsData?.total ?? 0} color="#10b981" />
        </div>

        {/* Badges */}
        <div>
          <div className="flex items-center justify-between mb-2 px-1">
            <h3 className="text-sm font-extrabold text-slate-700">{t("parent.badges", "Insigne")}</h3>
            <span className="text-xs font-bold text-slate-400 tabular-nums">
              {childBadges.length} {t("parent.badgesEarned", "obținute")}
            </span>
          </div>
          {childBadges.length === 0 ? (
            <GameCard className="p-6 text-center">
              <GameIcon name="medal" className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm text-slate-500">{t("parent.noBadges", "Nicio insignă obținută încă")}</p>
            </GameCard>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              {childBadges.map(ub => {
                const ring = TIER_RING[ub.badge.tier] || "from-indigo-400 to-blue-500";
                return (
                  <GameCard key={ub.id} className="p-3 flex flex-col items-center text-center">
                    <div className={`w-14 h-14 rounded-2xl grid place-items-center text-2xl bg-gradient-to-br ${ring} shadow-inner`}>
                      {ub.badge.icon_image_url
                        ? <img src={ub.badge.icon_image_url} alt="" className="w-8 h-8 object-contain" />
                        : <span>{ub.badge.icon_emoji || "🏅"}</span>}
                    </div>
                    <div className="text-[11px] font-bold text-slate-700 mt-2 leading-tight line-clamp-2">{ub.badge.name}</div>
                  </GameCard>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent quiz activity */}
        <div>
          <h3 className="text-sm font-extrabold text-slate-700 mb-2 px-1">{t("parent.recentQuizzes", "Quiz-uri recente")}</h3>
          {recentQuizzes.length === 0 ? (
            <GameCard className="p-6 text-center">
              <GameIcon name="bulb" className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm text-slate-500">{t("parent.noQuizzes", "Niciun quiz rezolvat încă")}</p>
            </GameCard>
          ) : (
            <GameCard className="divide-y divide-slate-100 overflow-hidden">
              {recentQuizzes.map(e => (
                <div key={e.id} className="flex items-center gap-3 p-4">
                  <div className="w-10 h-10 rounded-xl bg-violet-100 grid place-items-center shrink-0">
                    <GameIcon name="bulb" className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800 truncate">{e.description || t("points.quiz", "Quiz")}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {new Date(e.created_at).toLocaleDateString("ro-RO", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                  <div className={`font-extrabold tabular-nums ${e.amount >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                    {e.amount >= 0 ? "+" : ""}{e.amount}
                  </div>
                </div>
              ))}
            </GameCard>
          )}
        </div>
      </div>
    );
  };

  // Shared "child isn't in a club yet" empty state for the club-only
  // sub-views (sessions / attendance / metrics).
  const renderNoClub = (message: string) => (
    <GameCard className="p-8 text-center">
      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-blue-100 grid place-items-center">
        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h3 className="text-base font-bold text-slate-800 mb-1">{t("parent.noClubTitle", "Nu face parte dintr-un club")}</h3>
      <p className="text-sm text-slate-500">{message}</p>
    </GameCard>
  );

  const renderAttendanceView = () => {
    if (individual) return renderNoClub(t("parent.attendanceNoClub", "Prezența apare aici după ce copilul se alătură unui club și participă la antrenamente."));
    if (!attendanceData) return null;
    const { records, stats } = attendanceData;

    const getStatusIcon = (status: string) => {
      switch (status) {
        case "present":
          return (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          );
        case "late":
          return (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          );
        case "absent":
          return (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          );
        case "excused":
          return (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          );
        default:
          return null;
      }
    };

    return (
      <div className="space-y-5">
        {/* Attendance Rate Card */}
        <GameHero className="rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm">{t("attendance.rate", "Rata de prezență")}</p>
              <h2 className="text-4xl font-extrabold mt-1 tabular-nums">{Math.round(stats.attendance_rate)}%</h2>
              <p className="text-white/70 text-sm mt-1 tabular-nums">
                {stats.present + stats.late} / {stats.total} {t("attendance.sessions", "ședințe")}
              </p>
            </div>
            <RingProgress pct={stats.attendance_rate} size={80} stroke={8} color="#ffffff" track="rgba(255,255,255,0.25)">
              <GameIcon name="check" className="w-8 h-8 text-white" />
            </RingProgress>
          </div>
        </GameHero>

        {/* Stats Grid */}
        <div className="grid grid-cols-4 gap-2">
          <div className="p-3 rounded-xl text-center bg-white border border-slate-100 shadow-sm">
            <p className="text-xl font-bold text-emerald-600 tabular-nums">{stats.present}</p>
            <p className="text-xs mt-0.5 text-slate-500">{t("attendance.presentShort", "Prezent")}</p>
          </div>
          <div className="p-3 rounded-xl text-center bg-white border border-slate-100 shadow-sm">
            <p className="text-xl font-bold text-amber-600 tabular-nums">{stats.late}</p>
            <p className="text-xs mt-0.5 text-slate-500">{t("attendance.lateShort", "Întârziat")}</p>
          </div>
          <div className="p-3 rounded-xl text-center bg-white border border-slate-100 shadow-sm">
            <p className="text-xl font-bold text-red-600 tabular-nums">{stats.absent}</p>
            <p className="text-xs mt-0.5 text-slate-500">{t("attendance.absentShort", "Absent")}</p>
          </div>
          <div className="p-3 rounded-xl text-center bg-white border border-slate-100 shadow-sm">
            <p className="text-xl font-bold text-slate-600 tabular-nums">{stats.excused}</p>
            <p className="text-xs mt-0.5 text-slate-500">{t("attendance.excusedShort", "Motivat")}</p>
          </div>
        </div>

        {/* Records List */}
        {records.length === 0 ? (
          <GameCard className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-slate-800 mb-2">{t("attendance.noRecords", "Niciun istoric")}</h3>
            <p className="text-slate-500 text-sm">{t("attendance.noRecordsDesc", "Prezențele vor apărea aici")}</p>
          </GameCard>
        ) : (
          <GameCard className="overflow-hidden divide-y divide-slate-100">
            {records.map(record => {
              const config = getStatusConfig(record.status);
              return (
                <div key={record.id} className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${config.bg} ${config.text}`}>
                      {getStatusIcon(record.status)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <h4 className="font-semibold text-slate-800 truncate">{record.session?.title}</h4>
                          <p className="text-sm text-slate-500">{record.session?.team?.name}</p>
                        </div>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${config.bg} ${config.text}`}>
                          {config.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-2 text-sm text-slate-500">
                        <div className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span>
                            {new Date(record.session?.start_at || record.created_at).toLocaleDateString("ro-RO", {
                              weekday: "short", day: "numeric", month: "short"
                            })}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>
                            {new Date(record.session?.start_at || record.created_at).toLocaleTimeString("ro-RO", {
                              hour: "2-digit", minute: "2-digit"
                            })}
                          </span>
                        </div>
                      </div>
                      {record.reason && (
                        <p className="text-sm text-slate-500 mt-2 italic bg-slate-50 rounded-lg px-3 py-2">"{record.reason}"</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </GameCard>
        )}
      </div>
    );
  };

  const renderMetricsView = () => {
    if (individual) return renderNoClub(t("parent.metricsNoClub", "Evoluția fizică este măsurată de antrenor. Va apărea aici după ce copilul se alătură unui club."));
    if (metricsData.length === 0) {
      return (
        <GameCard className="p-8 text-center">
          <svg className="w-16 h-16 text-slate-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <p className="text-slate-500">{t("parent.noMetrics", "Nu există măsurători înregistrate")}</p>
        </GameCard>
      );
    }

    const latest = metricsData[0];

    return (
      <div className="space-y-4">
        <GameCard className="p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">{t("parent.latestMetrics", "Ultimele măsurători")}</h3>
          <div className="grid grid-cols-2 gap-4">
            {latest.height_cm && (
              <div className="bg-slate-50 rounded-xl p-3">
                <p className="text-xs text-slate-500">{t("metrics.height", "Înălțime")}</p>
                <p className="text-xl font-bold text-slate-800 tabular-nums">{latest.height_cm} cm</p>
              </div>
            )}
            {latest.weight_kg && (
              <div className="bg-slate-50 rounded-xl p-3">
                <p className="text-xs text-slate-500">{t("metrics.weight", "Greutate")}</p>
                <p className="text-xl font-bold text-slate-800 tabular-nums">{latest.weight_kg} kg</p>
              </div>
            )}
            {latest.wingspan_cm && (
              <div className="bg-slate-50 rounded-xl p-3">
                <p className="text-xs text-slate-500">{t("metrics.wingspan", "Anvergură")}</p>
                <p className="text-xl font-bold text-slate-800 tabular-nums">{latest.wingspan_cm} cm</p>
              </div>
            )}
            {latest.reach_cm && (
              <div className="bg-slate-50 rounded-xl p-3">
                <p className="text-xs text-slate-500">{t("metrics.reach", "Alonjă")}</p>
                <p className="text-xl font-bold text-slate-800 tabular-nums">{latest.reach_cm} cm</p>
              </div>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-3">
            {t("parent.measuredOn", "Măsurat pe")} {formatDate(new Date(latest.measured_at))}
          </p>
        </GameCard>

        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-slate-700">{t("parent.metricsHistory", "Istoricul măsurătorilor")}</h3>
          {metricsData.slice(1).map(metric => (
            <GameCard key={metric.id} depth={false} className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500">{formatDate(new Date(metric.measured_at))}</p>
                <div className="flex gap-3 text-sm tabular-nums">
                  {metric.height_cm && <span className="text-slate-600">{metric.height_cm}cm</span>}
                  {metric.weight_kg && <span className="text-slate-600">{metric.weight_kg}kg</span>}
                </div>
              </div>
            </GameCard>
          ))}
        </div>
      </div>
    );
  };

  const renderSessionsView = () => {
    if (individual) return renderNoClub(t("parent.sessionsNoClub", "Antrenamentele apar aici după ce copilul se alătură unui club."));
    if (sessionsData.length === 0) {
      return (
        <GameCard className="p-8 text-center">
          <svg className="w-16 h-16 text-slate-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="text-slate-500">{t("parent.noSessions", "Nu există antrenamente programate")}</p>
        </GameCard>
      );
    }

    return (
      <div className="space-y-3">
        {sessionsData.slice(0, 10).map(session => (
          <GameCard key={session.id} depth={false} className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-medium text-slate-800">{session.title}</p>
                <p className="text-xs text-slate-500 mt-1">
                  {new Date(session.start_at).toLocaleDateString("ro-RO", { weekday: "long", day: "numeric", month: "long" })}
                </p>
                <p className="text-xs text-slate-400 tabular-nums">
                  {new Date(session.start_at).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })} - {new Date(session.end_at).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
              <span className={`px-2 py-1 rounded-lg text-xs font-medium ${session.type === "sport" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>
                {session.type === "sport" ? t("session.sport", "Sport") : t("session.educational", "Educativ")}
              </span>
            </div>
          </GameCard>
        ))}
      </div>
    );
  };

  const getSourceIcon = (source: string) => {
    const map: Record<string, { bg: string; icon: React.ReactNode }> = {
      quiz: { bg: "bg-purple-100", icon: <GameIcon name="bulb" className="w-5 h-5" /> },
      attendance: { bg: "bg-emerald-100", icon: <GameIcon name="check" className="w-5 h-5" /> },
      video: { bg: "bg-blue-100", icon: <GameIcon name="bolt" className="w-5 h-5" /> },
      reward: { bg: "bg-amber-100", icon: <GameIcon name="star" className="w-5 h-5" /> },
      bonus: { bg: "bg-amber-100", icon: <GameIcon name="star" className="w-5 h-5" /> },
    };
    const cfg = map[source] || { bg: "bg-slate-100", icon: <GameIcon name="star" className="w-5 h-5" /> };
    return <div className={`w-10 h-10 rounded-xl ${cfg.bg} flex items-center justify-center`}>{cfg.icon}</div>;
  };

  const renderPointsView = () => {
    if (gamiLoading && !pointsData) {
      return <div className="space-y-4"><Skeleton className="h-44 rounded-2xl" /><Skeleton className="h-28 rounded-2xl" /></div>;
    }
    if (!pointsData) {
      return (
        <GameCard className="p-8 text-center">
          <GameIcon name="star" className="w-14 h-14 mx-auto mb-3 opacity-40" />
          <p className="text-slate-500">{t("parent.noPoints", "Nu există puncte înregistrate")}</p>
        </GameCard>
      );
    }

    return (
      <div className="space-y-5">
        {/* Total Points Card */}
        <div className="bg-gradient-to-br from-emerald-400 via-teal-500 to-cyan-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm">{t("points.total", "Total puncte")}</p>
              <h2 className="text-4xl font-extrabold mt-1 tabular-nums">{pointsData.total || 0}</h2>
            </div>
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <GameIcon name="star" className="w-9 h-9 text-white" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-5">
            <div className="bg-white/10 rounded-xl p-3 backdrop-blur">
              <p className="text-white/70 text-xs">{t("points.thisWeek", "Săptămâna asta")}</p>
              <p className="text-xl font-bold mt-0.5 tabular-nums">{pointsData.this_week || 0}</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3 backdrop-blur">
              <p className="text-white/70 text-xs">{t("points.thisMonth", "Luna asta")}</p>
              <p className="text-xl font-bold mt-0.5 tabular-nums">{pointsData.this_month || 0}</p>
            </div>
          </div>
        </div>

        {/* Breakdown */}
        {pointsData.breakdown && pointsData.breakdown.length > 0 && (
          <GameCard className="overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
              <h3 className="font-semibold text-slate-800">{t("points.breakdown", "Defalcare")}</h3>
            </div>
            <div className="p-4 grid grid-cols-2 gap-3">
              {pointsData.breakdown.map((item: any, idx: number) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                  {getSourceIcon(item.source)}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-slate-500 capitalize">{getSourceLabel(item.source)}</p>
                    <p className="font-bold text-slate-800 tabular-nums">{item.total}</p>
                  </div>
                </div>
              ))}
            </div>
          </GameCard>
        )}

        {/* History */}
        <GameCard className="overflow-hidden">
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
            <h3 className="font-semibold text-slate-800">{t("points.history", "Istoric")}</h3>
          </div>
          {pointsData.recent_history && pointsData.recent_history.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {pointsData.recent_history.slice(0, 15).map((entry: any) => (
                <div key={entry.id} className="flex items-center gap-3 p-4">
                  {getSourceIcon(entry.source)}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800">{getSourceLabel(entry.source)}</p>
                    {entry.description && <p className="text-sm text-slate-500 truncate">{entry.description}</p>}
                    <p className="text-xs text-slate-400 mt-0.5">
                      {new Date(entry.created_at).toLocaleDateString("ro-RO", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                  <div className={`font-bold tabular-nums ${entry.amount >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                    {entry.amount >= 0 ? "+" : ""}{entry.amount}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <GameIcon name="star" className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <h4 className="font-semibold text-slate-800 mb-1">{t("points.noHistory", "Niciun istoric")}</h4>
              <p className="text-slate-500 text-sm">{t("points.noHistoryDesc", "Punctele vor apărea aici")}</p>
            </div>
          )}
        </GameCard>
      </div>
    );
  };

  // ── Sub-view shell (non-home) ─────────────────────────────────────
  if (currentView !== "home") {
    const titles: Record<string, string> = {
      progress: t("parent.progress", "Progres"),
      attendance: t("parent.attendance", "Prezență"),
      metrics: t("parent.metrics", "Metrici"),
      sessions: t("parent.sessions", "Antrenamente"),
      points: t("parent.points", "Puncte"),
    };
    const subtitles: Record<string, string> = {
      progress: t("parent.progressSubtitle", "Nivel, insigne și quiz-uri"),
      attendance: t("parent.attendanceSubtitle", "Istoricul prezenței la antrenamente"),
      metrics: t("parent.metricsSubtitle", "Evoluția fizică a copilului"),
      sessions: t("parent.sessionsSubtitle", "Programul de antrenamente"),
      points: t("parent.pointsSubtitle", "Punctele câștigate"),
    };
    return (
      <MobileAppLayout>
        <div className="pb-6">
          {/* Header — same dark hero the athlete screens use, so every
              parent screen shares one visual language. No back arrow: these
              are top-level screens reachable straight from the app nav. */}
          <GameHero flow className="rounded-br-3xl px-4 pt-3 pb-4">
            <h1 className="text-xl font-extrabold truncate">{titles[currentView]}</h1>
            <p className="text-white/70 text-[13px] mt-0.5 truncate">{childName}{subtitles[currentView] ? ` · ${subtitles[currentView]}` : ""}</p>
          </GameHero>

          <div className="px-4 pt-5 space-y-4">
            {childSelector}
            {renderSubView()}
          </div>
        </div>
      </MobileAppLayout>
    );
  }

  // ── Home view ─────────────────────────────────────────────────────
  // For an individual child the home switches to a learning-first layout
  // and hides every club-dependent section (see `individual` above).
  const clubName = individual ? null : child?.athlete_profiles?.club?.name || null;
  const teamName = child?.athlete_profiles?.team?.name || null;
  const quizzesDone = (pointsData?.recent_history || []).filter((e) => e.source === "quiz").length;
  const attendancePct = homeAttendance ? Math.round(homeAttendance.attendance_rate) : null;

  // Two-line date/time helper for the "what's next" rows.
  const dayNum = (d: string) => new Date(d).getDate();
  const monShort = (d: string) => new Date(d).toLocaleString("ro-RO", { month: "short" });
  const timeStr = (d: string) => new Date(d).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" });

  // Trainings and events, merged into a single chronological "what's next"
  // list. They used to be two separate blocks that each repeated a heading, a
  // "see all" link and a card the bottom nav already leads to.
  type UpcomingItem = {
    key: string;
    date: string;
    time: string | null;
    title: string;
    place: string | null;
    badge: { label: string; className: string } | null;
    tone: "session" | "event";
    to: string;
  };
  const upcoming: UpcomingItem[] = [];
  if (!individual && homeNextSession) {
    upcoming.push({
      key: `s-${homeNextSession.id}`,
      date: homeNextSession.start_at,
      time: timeStr(homeNextSession.start_at),
      title: homeNextSession.title,
      place: homeNextSession.location_text || homeNextSession.team?.name || teamName || null,
      badge: { label: t("parent.training", "Antrenament"), className: "bg-blue-100 text-blue-700" },
      tone: "session",
      to: `/app/parent/sessions?child=${selectedChild?.athlete_id}`,
    });
  }
  if (!individual) {
    for (const inv of homeEvents) {
      const ev = inv.event!;
      upcoming.push({
        key: `e-${inv.id}`,
        date: ev.start_date,
        time: ev.event_time || null,
        title: ev.title,
        place: [ev.location_city, ev.location_county].filter(Boolean).join(", ") || ev.location_address || null,
        badge:
          inv.status === "confirmed"
            ? { label: t("events.inv_confirmed", "Confirmat"), className: "bg-emerald-100 text-emerald-700" }
            : { label: t("events.inv_invited", "Invitat"), className: "bg-amber-100 text-amber-700" },
        tone: "event",
        to: `/app/parent/events?child=${selectedChild?.athlete_id}`,
      });
    }
  }
  upcoming.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  const upcomingTop = upcoming.slice(0, 3);

  const progressHref = `/app/parent/progress?child=${selectedChild?.athlete_id}`;

  // Home is deliberately a summary, not a second copy of the app. Every
  // number below appears exactly ONCE and is itself the way into its own
  // screen — the old layout showed points, attendance, trainings, events and
  // metrics in a stat strip, again as full preview cards, and a third time in
  // an "Explorează" grid that also duplicated the bottom nav.
  return (
    <MobileAppLayout>
      <div className="px-4 py-6 space-y-5">
        {/* Child selector — switching kids is the first affordance. */}
        {childSelector}

        {searchParams.get("child_added") && (
          <div className="rounded-2xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800">
            {t("parent.childAdded", "Copilul a fost adăugat cu succes. Datele lui apar mai jos.")}
          </div>
        )}

        {/* Pending (unpaid) plan → offer to resume the Stripe checkout */}
        {selectedChild?.subscription?.status === "pending" &&
          selectedChild.subscription.plan_code !== "free" && (
            <div className="rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3">
              <p className="text-sm font-semibold text-amber-800">
                {t("parent.paymentPending", "Plata abonamentului pentru {{name}} nu a fost finalizată.", { name: selectedChild.athlete?.full_name || "" })}
              </p>
              <button
                onClick={() => handleResumePayment(selectedChild.athlete_id)}
                disabled={resuming}
                className="mt-2 w-full py-2.5 rounded-xl bg-amber-600 hover:bg-amber-500 disabled:opacity-60 text-white text-sm font-bold"
              >
                {resuming ? t("parent.redirecting", "Se redirecționează...") : t("parent.payNow", "Plătește acum")}
              </button>
            </div>
          )}

        {siblingDiscount > 0 && (
          <div className="rounded-2xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800 flex items-center gap-2">
            <GameIcon name="star" className="w-4 h-4 shrink-0" />
            {t("parent.siblingDiscountActive", "Ai {{pct}}% reducere de frați activă pe abonamentele copiilor tăi.", { pct: siblingDiscount })}
          </div>
        )}

        {/* Child hero — who this is, where they train, how far along they are.
            The whole card is the way into Progres, so no separate link or
            duplicate club card is needed. */}
        {child && (
          <Link to={progressHref} className="block">
            <GameCard depth className="relative overflow-hidden p-5 active:translate-y-[2px] transition-transform">
              <div className="pointer-events-none absolute -top-12 -right-10 w-44 h-44 rounded-full bg-indigo-100/70" />
              <div className="relative flex items-center gap-4">
                {child.profile_url ? (
                  <img src={child.profile_url} alt={child.full_name} className="w-16 h-16 rounded-2xl object-cover shrink-0 ring-1 ring-slate-200" />
                ) : (
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 grid place-items-center text-white text-2xl font-black shrink-0">
                    {child.full_name?.charAt(0) || "?"}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <h1 className="text-lg font-black text-slate-800 truncate">{child.full_name}</h1>
                  <p className="text-sm text-slate-500 truncate">
                    {individual
                      ? t("parent.individualAthlete", "Sportiv individual")
                      : [clubName, teamName].filter(Boolean).join(" · ") || t("parent.noTeamYet", "Fără echipă încă")}
                  </p>
                  {level ? (
                    <div className="mt-1 inline-flex items-center gap-1.5 text-xs font-bold text-indigo-600 bg-indigo-50 rounded-full px-2 py-0.5">
                      <GameIcon name="trophy" className="w-3.5 h-3.5" />
                      {t("parent.level", "Nivel")} {level.level}{level.title ? ` · ${level.title}` : ""}
                    </div>
                  ) : null}
                </div>
                <svg className="w-5 h-5 text-slate-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>

              <div className="relative mt-4 pt-4 border-t border-slate-100">
                {gamiLoading && !level ? (
                  <Skeleton className="h-8" />
                ) : level ? (
                  <LevelBar
                    level={level.level}
                    pct={level.progress_pct}
                    title={level.title}
                    xpInto={level.xp_into_level}
                    xpFor={level.xp_for_next}
                    className="w-full"
                  />
                ) : (
                  <p className="text-sm text-slate-400">{t("parent.noPoints", "Nu există puncte înregistrate")}</p>
                )}
              </div>
            </GameCard>
          </Link>
        )}

        {/* The numbers a parent actually checks, each one the entry to its
            own screen. Club child: puncte / prezență / antrenamente / metrici.
            Individual child: no club, so no attendance, trainings or
            coach-recorded measurements. */}
        <div className={`grid gap-2.5 ${individual ? "grid-cols-3" : "grid-cols-2"}`}>
          <StatTile
            icon="star"
            color="#f59e0b"
            label={t("parent.points", "Puncte")}
            value={(pointsData?.total ?? 0).toLocaleString("ro-RO")}
            onClick={() => navigate(`/app/parent/points?child=${selectedChild?.athlete_id}`)}
          />
          {individual ? (
            <>
              <StatTile
                icon="check"
                color="#6366f1"
                label={t("parent.quizzesDone", "Quiz-uri")}
                value={quizzesDone}
                onClick={() => navigate(progressHref)}
              />
              <StatTile
                icon="medal"
                color="#a78bfa"
                label={t("parent.badges", "Insigne")}
                value={childBadges.length}
                onClick={() => navigate(progressHref)}
              />
            </>
          ) : (
            <>
              <StatTile
                icon="check"
                color="#10b981"
                label={t("parent.attendance", "Prezență")}
                value={attendancePct !== null ? `${attendancePct}%` : "—"}
                onClick={() => navigate(`/app/parent/attendance?child=${selectedChild?.athlete_id}`)}
              />
              <StatTile
                icon="clock"
                color="#3b82f6"
                label={t("parent.sessions", "Antrenamente")}
                value={homeUpcomingCount}
                onClick={() => navigate(`/app/parent/sessions?child=${selectedChild?.athlete_id}`)}
              />
              <StatTile
                icon="bolt"
                color="#a855f7"
                label={t("parent.metrics", "Metrici")}
                value={homeMetric?.height_cm != null ? `${homeMetric.height_cm} cm` : "—"}
                onClick={() => navigate(`/app/parent/metrics?child=${selectedChild?.athlete_id}`)}
              />
            </>
          )}
        </div>

        {/* Individual child → learning is the heart of the screen. */}
        {individual && (
          <Link to={progressHref}>
            <div className="relative w-full overflow-hidden rounded-3xl p-5 text-left bg-gradient-to-br from-indigo-500 via-violet-500 to-fuchsia-500 text-white shadow-[0_10px_28px_-10px_rgba(139,92,246,0.7)] active:translate-y-[2px] transition-transform">
              <div className="pointer-events-none absolute -top-8 -right-6 w-32 h-32 rounded-full bg-white/15 blur-xl" />
              <div className="relative flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-white/20 grid place-items-center text-3xl shrink-0">🧠</div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10.5px] font-extrabold uppercase tracking-[0.14em] text-white/80">
                    {t("parent.learning", "Învățare")}
                  </div>
                  <div className="text-lg font-extrabold leading-tight">
                    {t("parent.learningTitle", "Progresul la quiz-uri")}
                  </div>
                  <div className="text-xs text-white/80 mt-0.5">
                    {quizzesDone > 0
                      ? t("parent.learningSubDone", "{{n}} quiz-uri rezolvate · nivel {{lvl}}", { n: quizzesDone, lvl: level?.level ?? 1 })
                      : t("parent.learningSubEmpty", "Quiz-uri, puncte și insigne — vezi cum evoluează")}
                  </div>
                </div>
              </div>
              <span className="relative mt-4 inline-flex items-center gap-2 bg-white text-indigo-700 font-extrabold text-sm px-4 py-2.5 rounded-2xl shadow-[0_4px_0_rgba(79,70,229,0.25)]">
                {t("parent.viewProgress", "Vezi progresul")} <GameIcon name="bolt" className="w-4 h-4" />
              </span>
            </div>
          </Link>
        )}

        {/* What's next — trainings and events in one chronological list. The
            dedicated screens hold the full history; Home shows only the next
            few things the parent has to plan around. */}
        {!individual && (
          <div>
            <h3 className="text-xs font-extrabold uppercase tracking-wide text-slate-400 mb-2.5 px-0.5">
              {t("parent.upNext", "Urmează")}
            </h3>
            {upcomingTop.length === 0 ? (
              <GameCard className="p-4 text-sm text-slate-500 flex items-center gap-3">
                <GameIcon name="clock" className="w-5 h-5 opacity-50" />
                {t("parent.nothingScheduled", "Nimic programat momentan.")}
              </GameCard>
            ) : (
              <div className="space-y-3">
                {upcomingTop.map((item) => (
                  <Link key={item.key} to={item.to}>
                    <GameCard depth className="flex items-center gap-3 p-4 active:translate-y-[2px] transition-transform">
                      <div
                        className={`w-12 h-12 rounded-2xl flex flex-col items-center justify-center shrink-0 ${
                          item.tone === "session" ? "bg-blue-100 text-blue-700" : "bg-fuchsia-100 text-fuchsia-700"
                        }`}
                      >
                        <span className="text-base font-black leading-none">{dayNum(item.date)}</span>
                        <span className="text-[9px] font-bold uppercase">{monShort(item.date)}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-extrabold text-slate-800 truncate">{item.title}</div>
                        {item.place && <div className="text-xs text-slate-500 mt-0.5 truncate">{item.place}</div>}
                        {item.badge && (
                          <span className={`inline-block mt-1 text-[10px] font-bold rounded-full px-2 py-0.5 ${item.badge.className}`}>
                            {item.badge.label}
                          </span>
                        )}
                      </div>
                      {item.time && (
                        <div className={`text-sm font-extrabold shrink-0 ${item.tone === "session" ? "text-indigo-600" : "text-fuchsia-600"}`}>
                          {item.time}
                        </div>
                      )}
                    </GameCard>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Individual child → explain what unlocks with a club. */}
        {individual && (
          <GameCard className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 grid place-items-center shrink-0">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="font-bold text-slate-800">{t("parent.noClubTitle", "Nu face parte dintr-un club")}</p>
                <p className="text-sm text-slate-500 mt-0.5">
                  {t("parent.noClubBody", "Antrenamentele, prezența, evenimentele și evoluția fizică apar aici după ce copilul se alătură unui club. Până atunci, poate învăța și câștiga puncte din quiz-uri.")}
                </p>
              </div>
            </div>
          </GameCard>
        )}

        {/* Money owed — the only thing on Home that needs the parent to act. */}
        {pendingPayments.length > 0 && (
          <div className="bg-amber-50 rounded-2xl p-5 border border-amber-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <GameIcon name="star" className="w-5 h-5" />
                <h3 className="font-semibold text-amber-800">{t("parent.pendingPayments", "Plăți în așteptare")}</h3>
              </div>
              <Link to={`/app/parent/payments?child=${selectedChild?.athlete_id}`} className="text-sm text-amber-700 font-medium hover:text-amber-800">
                {t("common.viewAll", "Vezi toate")}
              </Link>
            </div>
            <div className="space-y-3">
              {pendingPayments.map(payment => (
                <div key={payment.id} className={`bg-white rounded-xl p-4 ${payment.payment_status === "overdue" ? "border-2 border-red-300" : ""}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-800">{payment.product?.name}</p>
                      {payment.next_due_date && (
                        <p className={`text-xs mt-1 ${payment.payment_status === "overdue" ? "text-red-600 font-medium" : "text-amber-600"}`}>
                          {t("parent.dueDate", "Scadent")}: {formatDate(new Date(payment.next_due_date))}
                          {payment.payment_status === "overdue" && ` (${t("payment.overdue", "Întârziat")})`}
                        </p>
                      )}
                    </div>
                    <p className="text-lg font-bold text-slate-800 tabular-nums">
                      {formatCurrency(payment.product?.price_cents || 0, payment.product?.currency)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {clubPaymentMethods.length > 0 && (
              <div className="mt-4 p-3 bg-white rounded-xl">
                <h5 className="text-sm font-medium text-slate-700 mb-2">{t("payment.acceptedMethods", "Metode de plată acceptate")}:</h5>
                <div className="flex flex-wrap gap-2">
                  {clubPaymentMethods.includes("cash") && (
                    <span className="px-2.5 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600">{t("payment.cash", "Numerar")}</span>
                  )}
                  {clubPaymentMethods.includes("card") && (
                    <span className="px-2.5 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600">{t("payment.card", "Card")}</span>
                  )}
                  {clubPaymentMethods.includes("bank_transfer") && (
                    <span className="px-2.5 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600">{t("payment.bankTransfer", "Transfer bancar")}</span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* The destinations that are in neither the bottom nav nor the cards
            above. Everything else was removed as a duplicate. */}
        <div className="grid grid-cols-2 gap-3">
          <Link to="/app/parent/plans" className="col-span-2">
            <GameCard className="p-4 flex items-center gap-3 active:translate-y-[2px] transition-transform">
              <div className="w-10 h-10 rounded-xl bg-violet-100 grid place-items-center shrink-0">
                <GameIcon name="bolt" className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-bold text-slate-800 truncate">{t("plans.title", "Planuri copii")}</h3>
                <p className="text-xs text-slate-500 truncate">
                  {t("plans.homeHint", "Vezi planul fiecărui copil și fă upgrade")}
                </p>
              </div>
              <svg className="w-5 h-5 text-slate-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </GameCard>
          </Link>
          <Link to={`/app/parent/payments?child=${selectedChild?.athlete_id}`}>
            <GameCard className="p-4 h-full flex items-center gap-3 active:translate-y-[2px] transition-transform">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 grid place-items-center shrink-0">
                <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              </div>
              <h3 className="font-bold text-slate-800 truncate">{t("parent.payments", "Plăți")}</h3>
            </GameCard>
          </Link>
          <Link to="/app/lives">
            <GameCard className="p-4 h-full flex items-center gap-3 active:translate-y-[2px] transition-transform">
              <div className="w-10 h-10 rounded-xl bg-rose-100 grid place-items-center shrink-0">
                <GameIcon name="heart" className="w-5 h-5" />
              </div>
              <h3 className="font-bold text-slate-800 truncate">{t("parent.lives", "Vieți")}</h3>
            </GameCard>
          </Link>
        </div>

        {/* Add another child */}
        <Link
          to="/app/parent/add-child"
          className="flex items-center justify-center gap-2 w-full py-3.5 rounded-2xl bg-white border border-slate-200 shadow-[0_3px_0_#eef2f8] text-indigo-600 font-bold active:translate-y-[2px] transition-transform"
        >
          <span className="text-lg leading-none">＋</span>
          {t("parent.addChild", "Adaugă un copil")}
        </Link>
      </div>
    </MobileAppLayout>
  );
}
